import { useMemo } from 'react';
import {
    useQCockpitMonitor,
    useQCockpitV2Monitor,
    useQCockpitV3Monitor,
    useQCockpitV4Monitor,
    useChanakyaMonitor,
    useChanakyaInverseMonitor,
} from '../useQCockpitMonitor';
import { useVyasaMonitor } from '../../vyasa/useVyasaMonitor';
import { buildEventFeedRows } from '../strategyEventFeed';
import {
    buildMonitorSnapshotView,
    buildVyasaSnapshotMetrics,
} from '../strategySnapshotBuilders';
import {
    resolveInstanceContext,
    resolveStrategyMeta,
} from '../strategyInstanceMeta';

const MONITOR_BY_STRATEGY = {
    q_cobra: 'baseline',
    q_cobra_v2: 'v2',
    q_cobra_v3: 'v3',
    q_cobra_v4: 'v4',
    chanakya: 'chanakya',
    chanakya_inverse: 'chanakya_inverse',
};

function pickMonitorBundle(strategyId, monitors) {
    const key = MONITOR_BY_STRATEGY[strategyId];
    switch (key) {
        case 'v2':
            return monitors.v2;
        case 'v3':
            return monitors.v3;
        case 'v4':
            return monitors.v4;
        case 'chanakya':
            return monitors.chanakya;
        case 'chanakya_inverse':
            return monitors.chanakyaInverse;
        default:
            return monitors.baseline;
    }
}

export function useStrategyInstanceView(strategyId, clientId, enabled = true) {
    const baseline = useQCockpitMonitor();
    const v2 = useQCockpitV2Monitor();
    const v3 = useQCockpitV3Monitor();
    const v4 = useQCockpitV4Monitor();
    const chanakya = useChanakyaMonitor();
    const chanakyaInverse = useChanakyaInverseMonitor();
    const vyasaMonitor = useVyasaMonitor({ pollIntervalMs: 1000 });

    const monitors = useMemo(
        () => ({
            baseline,
            v2,
            v3,
            v4,
            chanakya,
            chanakyaInverse,
        }),
        [baseline, v2, v3, v4, chanakya, chanakyaInverse]
    );

    return useMemo(() => {
        if (!enabled || !strategyId || !clientId) {
            return null;
        }

        const meta = resolveStrategyMeta(strategyId);

        if (meta.family === 'vyasa') {
            const snapshot = buildVyasaSnapshotMetrics(vyasaMonitor);
            const vyasaEvents = Array.isArray(vyasaMonitor.events) ? vyasaMonitor.events : [];
            return {
                meta,
                strategyLabel: meta.label,
                connection: vyasaMonitor.connected ? 'connected' : 'disconnected',
                connectionTone: vyasaMonitor.connected ? 'success' : 'error',
                freshness: null,
                snapshot,
                eventFeedRows: vyasaEvents.slice(0, 10),
            };
        }

        const monitor = pickMonitorBundle(strategyId, monitors);
        const context = resolveInstanceContext(monitor.state, strategyId, clientId);
        const snapshot = buildMonitorSnapshotView({
            strategyFamily: meta.family,
            strategyLabel: meta.label,
            strategyId,
            summary: context.summary,
            latestLayer: context.latestLayer,
            latestActivity: context.latestActivity,
            account: context.account,
            legs: context.legs,
            positions: context.positions,
        });
        const eventFeedRows = buildEventFeedRows({
            summary: context.summary,
            events: context.events,
            positions: context.positions,
            latestLayer: context.latestLayer,
            latestActivity: context.latestActivity,
            layers: monitor.state?.layers || [],
        });
        const connectionTone = monitor.connection === 'connected'
            ? 'success'
            : monitor.connection === 'error'
                ? 'error'
                : 'warning';

        return {
            meta,
            strategyLabel: context.summary.strategyLabel || meta.label,
            connection: monitor.connection,
            connectionTone,
            freshness: monitor.freshness,
            snapshot,
            eventFeedRows,
        };
    }, [enabled, strategyId, clientId, monitors, vyasaMonitor]);
}

export default useStrategyInstanceView;
